import type { CoachingSessionDto } from "../../services/coaching-dtos";

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const TIME_PATTERN = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;
const NAIVE_START_PATTERN = /^(\d{4}-\d{2}-\d{2})T(\d{1,2}:\d{2}(?::\d{2})?)$/;
const ZONE_SUFFIX_PATTERN = /(?:[zZ]|[+-]\d{2}:?\d{2})$/;

function timeZoneOffsetMs(utcMs: number, timeZone: string): number {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
  const parts: Record<string, string> = {};
  for (const part of formatter.formatToParts(new Date(utcMs))) {
    if (part.type !== "literal") parts[part.type] = part.value;
  }
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );
  return asUtc - utcMs;
}

/** Convert a local wall time in an IANA timezone into its UTC instant. */
export function sessionStartInstantFromWallTime(date: string, time: string, timezone = "UTC"): Date | null {
  const day = date.trim();
  const clock = time.trim();
  if (!DATE_PATTERN.test(day)) return null;
  const timeMatch = TIME_PATTERN.exec(clock);
  if (!timeMatch) return null;

  const hours = Number(timeMatch[1]);
  const minutes = Number(timeMatch[2]);
  const seconds = Number(timeMatch[3] ?? 0);
  if (hours > 23 || minutes > 59 || seconds > 59) return null;

  const naiveUtc = Date.UTC(
    Number(day.slice(0, 4)),
    Number(day.slice(5, 7)) - 1,
    Number(day.slice(8, 10)),
    hours,
    minutes,
    seconds
  );
  if (!Number.isFinite(naiveUtc)) return null;
  if (new Date(naiveUtc).toISOString().slice(0, 10) !== day) return null;

  try {
    // Two-pass offset resolution keeps DST transitions deterministic, matching
    // the API's coachingWallTimeToUtc helper.
    const firstGuess = naiveUtc - timeZoneOffsetMs(naiveUtc, timezone.trim() || "UTC");
    const resolved = naiveUtc - timeZoneOffsetMs(firstGuess, timezone.trim() || "UTC");
    return new Date(resolved);
  } catch {
    return null;
  }
}

/** Return the stored session start as an instant, when it can be computed. */
export function sessionStartInstant(session: CoachingSessionDto): Date | null {
  const startsAt = session.starts_at?.trim();
  if (!startsAt) return null;

  if (ZONE_SUFFIX_PATTERN.test(startsAt)) {
    const direct = new Date(startsAt);
    return Number.isFinite(direct.getTime()) ? direct : null;
  }

  const naive = NAIVE_START_PATTERN.exec(startsAt);
  if (!naive) return null;
  return sessionStartInstantFromWallTime(naive[1], naive[2], session.timezone || "UTC");
}

export function hasSessionStarted(session: CoachingSessionDto, now = new Date()): boolean {
  const startsAt = session.starts_at?.trim() ?? "";
  // A session stored without a start time reads as a bare date. The API counts it as
  // passed once that date is before today (UTC), so the page does the same.
  if (DATE_PATTERN.test(startsAt)) return startsAt < now.toISOString().slice(0, 10);
  const start = sessionStartInstant(session);
  return start !== null && start.getTime() <= now.getTime();
}

/**
 * The stored start as wall-clock parts in the session's own timezone, for showing
 * the current schedule. A bare date has no time; a zoned instant is shown in the
 * session's timezone (UTC when that timezone can't be resolved).
 */
export function sessionWallClock(
  session: CoachingSessionDto
): { date: string; time: string | null; timezone: string } | null {
  const startsAt = session.starts_at?.trim();
  if (!startsAt) return null;
  const timezone = session.timezone?.trim() || "UTC";
  if (DATE_PATTERN.test(startsAt)) return { date: startsAt, time: null, timezone };

  const naive = NAIVE_START_PATTERN.exec(startsAt);
  if (naive) {
    const [hours, minutes] = naive[2].split(":");
    return { date: naive[1], time: `${hours.padStart(2, "0")}:${minutes}`, timezone };
  }

  const instant = sessionStartInstant(session);
  if (!instant) return null;
  try {
    const parts: Record<string, string> = {};
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
    for (const part of formatter.formatToParts(instant)) {
      if (part.type !== "literal") parts[part.type] = part.value;
    }
    return { date: `${parts.year}-${parts.month}-${parts.day}`, time: `${parts.hour}:${parts.minute}`, timezone };
  } catch {
    const iso = instant.toISOString();
    return { date: iso.slice(0, 10), time: iso.slice(11, 16), timezone: "UTC" };
  }
}
